import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../../services/window-information';
import Card from '../../../components/Card';
import MyAppText from '../../../components/MyAppText';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { translate } from '../../../services/translate';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import { useAuth } from '../../../context/Auth';
import Toast from 'react-native-toast-message';
import { dispatchService } from '../../../services/central-api/dispatch';
import { useQuery } from '@tanstack/react-query';

interface Props {
    isModalVisible: boolean;
    onClose: () => void;
    patrolIndicatorId?: number;
}

interface PatrolIndicatorForm {
    name: string;
    code: string;
    actingBodyId: number;
}

export default function PatrolIndicatorModal({ isModalVisible, onClose, patrolIndicatorId }: Props) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [isSendingData, setIsSendingData] = useState<boolean>(false);

    const defaultEmptyValues: PatrolIndicatorForm = {
        actingBodyId: 0,
        code: '',
        name: ''
    };
    const form = useForm<PatrolIndicatorForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const queryResult = useQuery({
        queryKey: ['getPatrolIndicatorById', patrolIndicatorId, isModalVisible],
        queryFn: () => dispatchService.getPatrolIndicatorById(patrolIndicatorId!),
        enabled: !!authenticatedUser && !!patrolIndicatorId && isModalVisible,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;
    const { data } = queryResult;

    function clearValues() {
        form.reset(defaultEmptyValues);
    }

    useEffect(() => {
        if (isModalVisible && authenticatedUser && !authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_patrol_indicators) {
            form.setValue('actingBodyId', authenticatedUser.actingBodyUnit.actingBodyId);
        }
    }, [isModalVisible, authenticatedUser]);

    useEffect(() => {
        if (!authenticatedUser || !data) {
            return;
        }
        form.reset({
            actingBodyId: data.actingBodyId,
            code: data.code,
            name: data.name
        });

    }, [data, authenticatedUser]);

    async function formSubmit() {
        setIsSendingData(true);
        try {
            const values = form.getValues();
            if (patrolIndicatorId) {
                await dispatchService.updatePatrolIndicator({
                    id: patrolIndicatorId,
                    name: values.name,
                    code: values.code,
                });
            } else {
                await dispatchService.createPatrolIndicator({
                    name: values.name,
                    code: values.code,
                    actingBodyId: values.actingBodyId
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            clearValues();
            onClose();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsSendingData(false);
        }
    }

    return (
        <Modal
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                clearValues();
                onClose();
            }}
            transparent={true}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: 450 }]}>
                    <Card style={{ padding: 0, gap: 8 }}>
                        <View style={{ flexDirection: 'column', rowGap: 8, paddingBottom: 24 }}>
                            <MyAppText style={styles.header}>{translate('registerIndicator')}</MyAppText>
                            <MyAppText style={styles.subheader}>{translate('fillTheFieldsToRegister')}</MyAppText>
                        </View>
                        <ActingBodiesDropdown
                            form={form}
                            fieldName='actingBodyId'
                            zIndex={2}
                            labelStyle={styles.fieldsLabelStyle}
                            viewStyle={{ gap: 8 }}
                            required
                            disabled={!!patrolIndicatorId || !authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_patrol_indicators}
                        />
                        <View style={{ flexDirection: 'row', gap: 12 }}>
                            <View style={{ flex: 1 }}>
                                <FormInput
                                    form={form}
                                    fieldName='code'
                                    required
                                    label={translate('code')}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={{ gap: 8 }}
                                />
                            </View>
                            <View style={{ flex: 2 }}>
                                <FormInput
                                    form={form}
                                    fieldName='name'
                                    required
                                    label={translate('name')}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={{ gap: 8 }}
                                />
                            </View>
                        </View>
                        <FormActions
                            viewStyle={{ flex: 1, justifyContent: 'space-around', paddingTop: 24 }}
                            buttonStyle={{ paddingHorizontal: 9, paddingVertical: 9 }}
                            buttonContentContainerStyle={{ flex: 1, }}
                            textStyle={{ fontSize: 16, fontWeight: '600' }}
                            onSubmit={formSubmit}
                            isLoading={isLoading || isSendingData}
                            onClose={() => {
                                onClose();
                                clearValues();
                            }}
                            disabled={!form.formState.isValid} />
                    </Card>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
}));
